import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Play,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Loader2,
  Circle,
  FlaskConical,
  FileText,
  AlertTriangle,
  Lock,
} from "lucide-react";
import type { HiveWorkflow, WfNodeKind } from "@/types";
import { useWorkflow, permissionFootprint } from "@/store/workflowStore";
import { nodeDef, KIND_META } from "@/data/mockWorkflowNodes";
import { Toggle } from "@/components/hive/shared";

type SimStatus = "pending" | "running" | "done" | "failed" | "skipped";
interface SimStep {
  id: string;
  label: string;
  kind: WfNodeKind;
  cost: number;
  output: string;
  status: SimStatus;
}

function orderedSteps(wf: HiveWorkflow): SimStep[] {
  const order = { hook: 0, process: 1, publish: 2 };
  return [...wf.nodes]
    .filter((n) => !n.disabled)
    .sort((a, b) => order[nodeDef(a.defKey).kind] - order[nodeDef(b.defKey).kind] || a.y - b.y)
    .map((n) => {
      const d = nodeDef(n.defKey);
      return {
        id: n.id,
        label: n.title,
        kind: d.kind,
        cost: d.budget ? Math.round((0.15 + Math.random() * 0.4) * 100) / 100 : 0,
        output: mockOutput(d.key),
        status: "pending" as SimStatus,
      };
    });
}

function mockOutput(key: string) {
  const m: Record<string, string> = {
    "proc.refine": "5 acceptance criteria · 0 open questions",
    "proc.run-ares": "+182 / −44 across 6 files",
    "proc.verify": "412 assertions · 0 regressions · coverage 86%",
    "proc.athena": "API readiness 94 · no drift",
    "proc.analyze-logs": "root cause: nil-pointer in retry path",
    "pub.github.pr": "PR #1843 (dry-run)",
    "pub.jira.update": "moved → In Review (dry-run)",
    "pub.slack.post": "message suppressed (sandbox)",
  };
  return m[key] ?? "ok";
}

export function WorkflowTestRunPanel({ wf }: { wf: HiveWorkflow }) {
  const runTest = useWorkflow((s) => s.runTest);
  const testPending = useWorkflow((s) => s.testPending);
  const ackTest = useWorkflow((s) => s.ackTest);
  const [steps, setSteps] = useState<SimStep[]>([]);
  const [phase, setPhase] = useState<"idle" | "running" | "done" | "failed">("idle");
  const [simFail, setSimFail] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const fp = permissionFootprint(wf);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);
  // top-bar "Run Test" / palette request → auto-run once, even if this panel
  // only mounted because of that request (the old nonce-diff missed that case).
  useEffect(() => {
    if (testPending) {
      ackTest();
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testPending]);
  // reset when switching workflows
  useEffect(() => {
    if (timer.current) clearInterval(timer.current);
    setSteps([]);
    setPhase("idle");
  }, [wf.id]);

  function run() {
    if (timer.current) clearInterval(timer.current);
    const base = orderedSteps(wf);
    const failIdx = simFail ? Math.min(base.length - 1, Math.max(1, Math.floor(base.length * 0.6))) : -1;
    setSteps(base);
    setPhase("running");
    runTest(wf.id); // mirror into the Execution Center
    let i = 0;
    timer.current = setInterval(() => {
      setSteps((prev) => {
        const next = prev.map((s, idx) => {
          if (idx < i) return s.status === "pending" ? { ...s, status: "done" as SimStatus } : s;
          if (idx === i) return { ...s, status: (idx === failIdx ? "failed" : "running") as SimStatus };
          return s;
        });
        return next;
      });
      if (i === failIdx) {
        setPhase("failed");
        if (timer.current) clearInterval(timer.current);
        setSteps((prev) => prev.map((s, idx) => (idx > failIdx ? { ...s, status: "skipped" } : s)));
        return;
      }
      if (i >= base.length - 1) {
        setSteps((prev) => prev.map((s) => (s.status === "running" || s.status === "pending" ? { ...s, status: "done" } : s)));
        setPhase("done");
        if (timer.current) clearInterval(timer.current);
        return;
      }
      i += 1;
    }, 650);
  }

  const cost = steps.filter((s) => s.status === "done").reduce((n, s) => n + s.cost, 0);
  const artifacts = wf.nodes
    .filter((n) => !n.disabled && ["pub.artifact", "pub.release", "proc.release-notes", "proc.test-plan"].includes(n.defKey))
    .map((n) => nodeDef(n.defKey).label);

  if (phase === "idle")
    return (
      <div className="grid h-full place-items-center p-4 text-center">
        <div>
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-rose-500/10">
            <FlaskConical size={22} className="text-rose-500" />
          </div>
          <div className="mt-3 text-sm font-medium text-app">Run a sandbox test</div>
          <div className="mx-auto mt-1 max-w-sm text-xs text-app-2">
            Simulates the workflow against a sample event — step output, permissions, cost and generated artifacts. No external systems are touched.
          </div>
          <div className="mt-3 flex items-center justify-center gap-3">
            <button onClick={run} className="flex items-center gap-1.5 rounded-lg bg-rose-500 px-4 py-2 text-xs font-semibold text-white transition hover:brightness-110">
              <Play size={13} /> Run sandbox test
            </button>
            <label className="flex items-center gap-1.5 text-[11px] text-app-2">
              <Toggle checked={simFail} onChange={setSimFail} color="#f43f5e" /> simulate failure
            </label>
          </div>
        </div>
      </div>
    );

  return (
    <div className="grid h-full grid-cols-[1fr,260px] divide-x divide-[hsl(var(--border))]">
      {/* timeline */}
      <div className="overflow-y-auto no-scrollbar p-3">
        <div className="mb-2 flex items-center gap-2">
          <StatusChip phase={phase} />
          <button onClick={run} className="ml-auto flex items-center gap-1 rounded-lg border border-app bg-surface-2/60 px-2.5 py-1 text-[11px] font-medium text-app-1 transition hover:border-app-strong">
            <RotateCcw size={11} /> Re-run
          </button>
        </div>
        <div className="space-y-1">
          {steps.map((s, i) => {
            const kc = KIND_META[s.kind].color;
            return (
              <motion.div key={s.id} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5" style={{ background: s.status === "running" ? `${kc}10` : undefined }}>
                <StepIcon status={s.status} />
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: kc }} />
                <span className={`flex-1 truncate text-xs ${s.status === "skipped" ? "text-app-2 line-through" : "text-app-1"}`}>{s.label}</span>
                {s.status === "done" && <span className="truncate text-[10px] text-app-2">{s.output}</span>}
                {s.status === "failed" && <span className="text-[10px] font-medium text-rose-500">failed · OOM</span>}
                {s.cost > 0 && s.status === "done" && <span className="text-[10px] tabular-nums text-app-2">${s.cost.toFixed(2)}</span>}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* side summary */}
      <div className="space-y-3 overflow-y-auto no-scrollbar p-3">
        <SideStat label="Estimated cost" value={`$${cost.toFixed(2)}`} sub={`BYONC saved ~$${wf.cost.byoncSavings.toFixed(2)}`} />
        <div>
          <Label>Permissions used</Label>
          <div className="flex flex-wrap gap-1">
            {[...fp.reads, ...fp.writes].slice(0, 6).map((p) => (
              <span key={p} className="rounded border border-app bg-surface-2/60 px-1.5 py-0.5 font-mono text-[9px] text-app-1">{p}</span>
            ))}
          </div>
        </div>
        {artifacts.length > 0 && (
          <div>
            <Label>Generated artifacts</Label>
            <div className="space-y-1">
              {artifacts.map((a) => (
                <div key={a} className="flex items-center gap-1.5 text-[11px] text-app-1"><FileText size={11} className="text-app-2" /> {a}</div>
              ))}
            </div>
          </div>
        )}
        <div>
          <Label>Warnings</Label>
          {fp.external ? (
            <div className="flex items-start gap-1.5 text-[10px] text-amber-500"><Lock size={11} className="mt-0.5 shrink-0" /> External posting suppressed in sandbox (dry-run).</div>
          ) : (
            <div className="text-[11px] text-app-2">No warnings.</div>
          )}
          {phase === "failed" && (
            <div className="mt-1 flex items-start gap-1.5 text-[10px] text-rose-500"><AlertTriangle size={11} className="mt-0.5 shrink-0" /> Run failed — workspace OOM during build. Recovery: increase compute or retry.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepIcon({ status }: { status: SimStatus }) {
  if (status === "done") return <CheckCircle2 size={14} className="text-emerald-500" />;
  if (status === "failed") return <XCircle size={14} className="text-rose-500" />;
  if (status === "running") return <Loader2 size={14} className="animate-spin text-cyan-400" />;
  if (status === "skipped") return <Circle size={14} className="text-app-2" />;
  return <Circle size={14} className="text-app-2/40" />;
}

function StatusChip({ phase }: { phase: "idle" | "running" | "done" | "failed" }) {
  const map = {
    running: { label: "Simulating…", color: "#22d3ee" },
    done: { label: "Completed", color: "#22c55e" },
    failed: { label: "Failed", color: "#f43f5e" },
    idle: { label: "Idle", color: "#64748b" },
  } as const;
  const m = map[phase];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold" style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}1a` }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} /> {m.label}
    </span>
  );
}

function SideStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-xl border border-app bg-surface-2/40 p-2.5">
      <Label>{label}</Label>
      <div className="text-lg font-semibold tabular-nums text-app">{value}</div>
      <div className="text-[10px] text-emerald-500">{sub}</div>
    </div>
  );
}
function Label({ children }: { children: React.ReactNode }) {
  return <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-app-2">{children}</div>;
}
